// Persistent history of AI actions the agent applied (create/update client or
// policy, bulk changes). Stored in localStorage so the "Historial" tab and the
// "Deshacer" button survive a page refresh — the in-chat card state does not.
//
// Storage layout (fixes "only the first action shows"): the SUMMARY list is kept
// small in one key and always saves; each undo PAYLOAD (which can be large — a
// bulk snapshot of up to 1000 policies) lives in its OWN key so it can never
// blow the list's quota. If a payload doesn't fit, that one entry just loses its
// Undo, but every action still appears in the history.
//
// Scope: per-browser (not synced). The permanent server audit lives in Actividades.

const KEY = 'deskte_ai_action_history_v1';
const UNDO_PREFIX = 'deskte_ai_undo_v1_';
const MAX = 100;      // summaries kept
const MAX_UNDO = 30;  // undo payloads kept (newest)

export function getAiActions() {
  try {
    const raw = localStorage.getItem(KEY);
    const a = raw ? JSON.parse(raw) : [];
    return Array.isArray(a) ? a : [];
  } catch {
    return [];
  }
}

function saveList(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    return true;
  } catch {
    return false;
  }
}

function removeUndo(id) {
  try { localStorage.removeItem(UNDO_PREFIX + id); } catch { /* ignore */ }
}

// Drop the oldest undo payloads, keeping the newest `keep` undoable entries.
function pruneUndo(list, keep = MAX_UNDO) {
  const undoableIds = list.filter((e) => e && e.undoable).map((e) => e.id);
  for (const id of undoableIds.slice(keep)) removeUndo(id);
}

function trySaveUndo(id, payload) {
  try {
    localStorage.setItem(UNDO_PREFIX + id, JSON.stringify(payload));
    return true;
  } catch {
    // Free space by dropping older payloads, then retry once.
    pruneUndo(getAiActions(), 5);
    try {
      localStorage.setItem(UNDO_PREFIX + id, JSON.stringify(payload));
      return true;
    } catch {
      return false;
    }
  }
}

export function getUndoPayload(id) {
  try {
    const raw = localStorage.getItem(UNDO_PREFIX + id);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Append a new action (newest first). `undoPayload` (optional) is stored apart.
// `entry.undoable` should be true when an undo payload is provided.
export function recordAiAction(entry, undoPayload) {
  const list = getAiActions();
  list.unshift(entry);
  saveList(list); // small — succeeds even when payloads are large
  if (entry.undoable && undoPayload) {
    const ok = trySaveUndo(entry.id, undoPayload);
    if (!ok) updateAiAction(entry.id, { undoable: false });
    else pruneUndo(getAiActions());
  }
  return entry;
}

// Patch an existing summary entry by id (e.g. mark it undone).
export function updateAiAction(id, patch) {
  const list = getAiActions().map((e) => (e && e.id === id ? { ...e, ...patch } : e));
  saveList(list);
  if (patch && patch.undoable === false) removeUndo(id);
  return list;
}

export function clearAiActions() {
  const list = getAiActions();
  for (const e of list) if (e && e.id) removeUndo(e.id);
  saveList([]);
}
